import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import { Route, Routes } from "react-router-dom";
import "../../index.css";

import { Grid, IconButton, Menu, MenuItem, Typography } from "@mui/material";

import { Menu as MenuIcon } from "@mui/icons-material";
import Notifications from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import Settings from "@mui/icons-material/Settings";
import AccountCircle from "@mui/icons-material/AccountCircle";

import Navigation from "../Navigation/Navigation";

import Home from "../../components/Home/Home";
import UploadedImages from "../../components/Images/UploadedImages";
import ProcessedImages from "../../components/Images/ProcessedImages";
import APIKeys from "../../components/APIKeys/APIKeys";
import Admin from "../../components/Admin/Admin";

import HttpContext from "../../contexts/HTTP/HttpContext";
import ConfigContext from "../../contexts/Config/ConfigContext";
import ImageContext from "../../contexts/Image/ImageContext";
import MobileContext from "../../contexts/Mobile/MobileContext";
import ModelContext from "../../contexts/Model/ModelContext";

import { useTheme } from "@mui/material/styles";

import useSocket from "../../hooks/useSocket";

function Dashboard() {
  const theme = useTheme();
  const { get } = useContext(HttpContext);
  const { setConfig } = useContext(ConfigContext);
  const { setModels } = useContext(ModelContext);
  const { setImageData } = useContext(ImageContext);
  const { isMobile } = useContext(MobileContext);

  const [imagesLoading, setImagesLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [modelsLoading, setModelsLoading] = useState(true);

  const [showNavigation, setShowNavigation] = useState(!isMobile);

  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [notificationsMenuAnchor, setNotificationsMenuAnchor] = useState(null);

  const [notifications, setNotifications] = useState([]);

  const {} = useSocket({ notifications, setNotifications });

  const logout = async () => {
    await get("/logout");

    localStorage.setItem("session_key", null);
    window.location.href = "/login";
  };

  const loadConfig = async () => {
    const res = await get("/userConfig");

    if (res.status === 200) {
      setConfig(res.data.config);
    }
  };

  const loadModels = async () => {
    const res = await get("/availableModels");

    if (res.status === 200) {
      setModels(res.data.models);
    }
  };

  const loadRecent = async () => {
    let rawImages = [];
    let processedImages = [];

    const uploadedImagesRes = await get("/getUploadedImages", { count: 50 });
    if (uploadedImagesRes.status === 200) {
      rawImages = _.get(uploadedImagesRes, "data.imageData", []);
    }

    const processedImagesRes = await get("/getProcessedImages", { count: 50 });
    if (processedImagesRes.status === 200) {
      processedImages = _.get(processedImagesRes, "data.imageData", []);
    }

    setImageData((imageData) => {
      const updatedImageData = _.cloneDeep(imageData);

      updatedImageData.raw = rawImages;
      updatedImageData.processed = processedImages;

      return updatedImageData;
    });
  };

  useEffect(() => {
    (async () => {
      await loadConfig();
      setConfigLoading(false);

      await loadModels();
      setModelsLoading(false);

      await loadRecent();
      setImagesLoading(false);
    })();
  }, []);

  return (
    <Grid
      style={{
        margin: 0,
        padding: 0,
        minWidth: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        style={{
          margin: 0,
          minWidth: "100%",
          padding: theme.spacing(2),
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="toggle navigation menu"
            disabled={configLoading || imagesLoading}
            onClick={() => setShowNavigation(!showNavigation)}
            style={{ marginRight: theme.spacing(2) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h4">BCIP</Typography>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="toggle notifications"
            disabled={configLoading || imagesLoading}
            onClick={(e) => setNotificationsMenuAnchor(e.currentTarget)}
            style={{
              marginRight: theme.spacing(2),
            }}
            className={notifications.length > 0 ? "pulsating-icon" : ""}
          >
            {notifications.length > 0 ? (
              <NotificationsActiveIcon style={{ color: "white" }} />
            ) : (
              <Notifications />
            )}
          </IconButton>
          <Menu
            anchorEl={notificationsMenuAnchor}
            open={Boolean(notificationsMenuAnchor)}
            onClose={() => {
              setNotificationsMenuAnchor(null);
              setTimeout(() => {
                setNotifications([]);
              }, 100);
            }}
          >
            {notifications.length > 0 &&
              notifications.map((notification) => {
                return <MenuItem>{notification}</MenuItem>;
              })}
            {notifications.length == 0 && (
              <MenuItem>No new notifications!</MenuItem>
            )}
          </Menu>
          <IconButton
            aria-label="toggle settings"
            disabled={configLoading || imagesLoading}
            onClick={(e) => {
              e.preventDefault();
            }}
            style={{ marginRight: theme.spacing(2) }}
          >
            <Settings />
          </IconButton>
          <IconButton
            aria-label="toggle account"
            disabled={configLoading || imagesLoading}
            onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
            style={{ marginRight: theme.spacing(2) }}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={() => setAccountMenuAnchor(null)}
          >
            <MenuItem onClick={logout}>Logout</MenuItem>
          </Menu>
        </div>
      </Grid>
      <Grid
        style={{
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "row",
          minWidth: "100%",
        }}
      >
        <Navigation
          visible={showNavigation}
          handleClose={() => setShowNavigation(false)}
        />
        <Routes>
          <Route
            path="uploads"
            element={<UploadedImages imagesLoading={imagesLoading} />}
          />
          <Route
            path="processed"
            element={<ProcessedImages imagesLoading={imagesLoading} />}
          />
          <Route path="api" element={<APIKeys />} />
          <Route path="admin" element={<Admin />} />
          <Route
            path="/"
            element={
              <Home
                imagesLoading={imagesLoading}
                configLoading={configLoading}
                modelsLoading={modelsLoading}
              />
            }
          />
          <Route
            path="*"
            element={
              <Home
                imagesLoading={imagesLoading}
                configLoading={configLoading}
                modelsLoading={modelsLoading}
              />
            }
          />
        </Routes>
      </Grid>
    </Grid>
  );
}

export default Dashboard;
